import { useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  User as FirebaseUser
} from 'firebase/auth';
import { auth } from '../services/firebase';
import { userService } from '../services/userService';
import { User } from '../services/userService';

export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // console.log('Auth state changed:', { user: user?.uid, loading: true });
      setCurrentUser(user);
      
      if (user) {
        try {
          // Get user profile from Firestore
          const profile = await userService.getUser(user.uid);
          // console.log('User profile loaded:', profile?.profile.firstName);
          setUserProfile(profile);
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      } else {
        // No user logged in
        setUserProfile(null);
      }
      
      setLoading(false);
      // console.log('Auth state updated:', { user: user?.uid, loading: false });
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (error) {
      throw error;
    }
  };

  const signUp = async (email: string, password: string, userData: Omit<User, 'id' | 'profile.createdAt' | 'profile.updatedAt' | 'profile.lastActive' | 'stats'>) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create user profile in Firestore
      if (result.user) {
        await userService.createUser(result.user.uid, {
          ...userData,
          profile: {
            ...userData.profile,
            email: result.user.email || email
          }
        });
      }
      
      return result;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  };

  const forceLogout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserProfile(null);
      setLoading(false);
      console.log('Force logout completed');
    } catch (error) {
      console.error('Force logout error:', error);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      // Get the current origin (works for both localhost and production)
      const currentOrigin = window.location.origin;
      const redirectUrl = `${currentOrigin}/signin`;
      
      // Configure action code settings for password reset
      const actionCodeSettings = {
        url: redirectUrl,
        handleCodeInApp: false, // Set to false for web apps
      };

      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      console.log('Password reset email sent successfully to:', email);
    } catch (error: any) {
      console.error('Password reset error:', error);
      // Provide more helpful error messages
      if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many requests. Please try again later.');
      } else {
        throw new Error(error.message || 'Failed to send password reset email. Please try again.');
      }
    }
  };

  return {
    currentUser,
    userProfile,
    loading,
    signIn,
    signUp,
    logout,
    forceLogout,
    resetPassword
  };
}; 